"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import Image from "next/image";
import { Mail, Eye, EyeOff } from "lucide-react";
import Link from "next/link";
import { useLogo } from "@/lib/useLogo";
import { useT } from "@/lib/cms-locale";

export default function LoginPage() {
  const router = useRouter();
  const { logoUrl, loginBgUrl } = useLogo();
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (res.ok && data.redirect) {
        if (data.sprache) {
          localStorage.setItem("cms-locale", data.sprache);
        }
        router.push(data.redirect);
      } else {
        setError(data.error || t("auth.loginError"));
      }
    } catch {
      setError(t("auth.connectionError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center">
      {/* Fullscreen background */}
      {loginBgUrl ? (
        <>
          <Image
            src={loginBgUrl}
            alt=""
            fill
            className="object-cover"
            priority
            unoptimized
          />
          <div className="absolute inset-0 bg-black/50" />
        </>
      ) : (
        <div className="absolute inset-0 bg-sidebar" />
      )}

      {/* Login box */}
      <div className="relative z-10 w-full max-w-[420px] rounded-2xl bg-black/40 px-10 py-12 shadow-2xl backdrop-blur-md mx-4">
        {/* Logo */}
        <div className="mb-8 text-center">
          {logoUrl ? (
            <div className="mx-auto mb-4 flex items-center justify-center">
              <Image src={logoUrl} alt="Logo" width={140} height={42} className="h-[42px] w-auto object-contain" unoptimized />
            </div>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15">
                <span className="text-xl font-bold text-primary">L</span>
              </div>
              <h1 className="text-xl font-bold text-white">lividea</h1>
            </>
          )}
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* E-Mail */}
          <div>
            <label htmlFor="email" className="mb-1 block text-xs font-medium text-white/60">
              {t("auth.email")}
            </label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                className="w-full rounded-lg border border-white/20 bg-white pl-10 pr-3 py-2.5 text-sm text-foreground placeholder-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t("auth.emailPlaceholder")}
              />
            </div>
          </div>

          {/* Passwort */}
          <div>
            <label htmlFor="password" className="mb-1 block text-xs font-medium text-white/60">
              {t("auth.password")}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full rounded-lg border border-white/20 bg-white px-3 py-2.5 pr-10 text-sm text-foreground placeholder-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t("auth.passwordPlaceholder")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="mt-1 text-right">
              <Link
                href="/forgot-password"
                className="text-xs text-white/50 hover:text-primary"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-sidebar transition-colors hover:bg-primary/80 disabled:opacity-50"
          >
            {loading ? t("auth.loggingIn") : t("auth.login")}
          </button>
        </form>
      </div>
    </div>
  );
}
